import { buildPlannedWorkApiFields, getPlannedWorkQuantityByType, parsePlannedWorkItemsFromRecord } from './plannedWorkItems';

export const JOINTS_PLANNED_WORK_TYPE = 'Field Joints';

export const YES_NO_OPTIONS = [
  { value: 'Yes', label: 'Yes' },
  { value: 'No', label: 'No' },
];

const toNullableNumber = (s) => {
  const t = (s ?? '').toString().trim();
  if (t === '') return null;
  const n = parseFloat(t);
  return Number.isNaN(n) ? null : n;
};

export const parseWallThicknessFromRecord = (record) => {
  if (!record) return [''];
  const multi = record.wallThicknessValues ?? record.wall_thickness_values;
  if (Array.isArray(multi) && multi.length) {
    const vals = multi.map((v) => (v != null && v !== '' ? String(v) : ''));
    return vals.some((v) => v.trim()) ? vals : [''];
  }
  const single = record.wallThicknessMm ?? record.wall_thickness_mm;
  if (single != null && single !== '') return [String(single)];
  return [''];
};

export const formatWallThicknessDisplay = (record) => {
  const vals = parseWallThicknessFromRecord(record)
    .map((v) => String(v).trim())
    .filter(Boolean);
  if (vals.length) return vals.join(', ');
  const n = record?.wallThicknessMm ?? record?.wall_thickness_mm;
  if (n != null && n !== '') return String(n);
  return '—';
};

export const buildWallThicknessApiFields = (values) => {
  const nums = (Array.isArray(values) ? values : [])
    .map((v) => toNullableNumber(v))
    .filter((n) => n != null);
  if (!nums.length) return {};
  if (nums.length === 1) return { wallThicknessMm: nums[0] };
  return { wallThicknessMm: nums[0], wallThicknessValues: nums };
};

export const parseExpectedJointsFromRecord = (record) => {
  const fromItems = getPlannedWorkQuantityByType(record, JOINTS_PLANNED_WORK_TYPE, {
    plannedWorkItemsOnly: true,
  });
  if (fromItems != null) return String(fromItems);

  const items = parsePlannedWorkItemsFromRecord(record);
  const jointItem = items.find((it) => {
    const wt = String(it.workType || '').trim().toLowerCase();
    return wt === 'field joints' || wt === 'joints';
  });
  if (jointItem?.quantity != null && String(jointItem.quantity).trim() !== '') {
    return String(jointItem.quantity);
  }
  const legacy = record?.plannedWelds ?? record?.planned_welds;
  if (legacy != null && legacy !== '') return String(legacy);
  return '';
};

export const getExpectedJointsQuantityForRecord = (record) => {
  const str = parseExpectedJointsFromRecord(record);
  if (!str) return null;
  const n = parseFloat(String(str).trim());
  return Number.isFinite(n) ? n : null;
};

export const sumExpectedJointsAcrossRecords = (records) => {
  if (!Array.isArray(records)) return null;
  let sum = 0;
  let any = false;
  records.forEach((r) => {
    const q = getExpectedJointsQuantityForRecord(r);
    if (q != null) {
      sum += q;
      any = true;
    }
  });
  return any ? sum : null;
};

export const formatExpectedJointsChipParts = (quantity) => {
  const n = typeof quantity === 'number' ? quantity : parseFloat(String(quantity));
  const qtyDisplay = Number.isFinite(n)
    ? Number.isInteger(n)
      ? String(n)
      : n.toLocaleString(undefined, { maximumFractionDigits: 3 })
    : String(quantity ?? '');
  return {
    label: 'EXPECTED JOINTS',
    value: qtyDisplay ? `${qtyDisplay} joints` : '—',
  };
};

export const buildJointsPlannedWorkFields = (expectedJoints) => {
  const t = String(expectedJoints ?? '').trim();
  if (!t) return {};
  return buildPlannedWorkApiFields([
    { workType: JOINTS_PLANNED_WORK_TYPE, quantity: t },
  ]);
};

export const parseSowPipelineExtrasFromRecord = (record) => {
  if (!record) {
    return {
      sectionId: '',
      rootWeldingTechnique: '',
      hasInternalLiner: undefined,
      internalLinerType: '',
      hasInternalCoating: undefined,
      internalCoatingType: '',
    };
  }
  const liner = record.hasInternalLiner ?? record.has_internal_liner;
  const coating = record.hasInternalCoating ?? record.has_internal_coating;
  return {
    sectionId:
      record.scheduleId ??
      record.schedule_id ??
      record.sectionId ??
      record.section_id ??
      '',
    rootWeldingTechnique:
      record.rootWeldingTechnique ?? record.root_welding_technique ?? '',
    hasInternalLiner:
      liner === true || liner === 'Yes'
        ? 'Yes'
        : liner === false || liner === 'No'
          ? 'No'
          : undefined,
    internalLinerType: record.internalLinerType ?? record.internal_liner_type ?? '',
    hasInternalCoating:
      coating === true || coating === 'Yes'
        ? 'Yes'
        : coating === false || coating === 'No'
          ? 'No'
          : undefined,
    internalCoatingType:
      record.internalCoatingType ?? record.internal_coating_type ?? '',
  };
};

export const buildSowPipelineExtras = ({
  sectionId,
  rootWeldingTechnique,
  hasInternalLiner,
  internalLinerType,
  hasInternalCoating,
  internalCoatingType,
}) => {
  const extras = {};
  const sid = String(sectionId ?? '').trim();
  if (sid) extras.scheduleId = sid;
  const root = String(rootWeldingTechnique ?? '').trim();
  if (root) extras.rootWeldingTechnique = root;
  if (hasInternalLiner === 'Yes') {
    extras.hasInternalLiner = true;
    const lt = String(internalLinerType ?? '').trim();
    if (lt) extras.internalLinerType = lt;
  } else if (hasInternalLiner === 'No') {
    extras.hasInternalLiner = false;
  }
  if (hasInternalCoating === 'Yes') {
    extras.hasInternalCoating = true;
    const ct = String(internalCoatingType ?? '').trim();
    if (ct) extras.internalCoatingType = ct;
  } else if (hasInternalCoating === 'No') {
    extras.hasInternalCoating = false;
  }
  return extras;
};

export const readSowSectionId = (record) =>
  record?.scheduleId ??
  record?.schedule_id ??
  record?.sectionId ??
  record?.section_id ??
  '';

const readSowNumericField = (record, camel, snake) => {
  if (!record) return null;
  const v = record[camel] ?? record[snake];
  if (v == null || v === '') return null;
  const n = typeof v === 'number' ? v : parseFloat(String(v));
  return Number.isFinite(n) ? n : null;
};

/** Hidden-field values to re-send when editing an offshore pipeline (excludes stalk/pipeStringId). */
export const pickOffshorePipelinePreserveFields = (record) => {
  if (!record || typeof record !== 'object') return {};
  const out = {};

  const material = String(record.material ?? '').trim();
  if (material) out.material = material;

  const fieldJointType = String(record.fieldJointType ?? record.field_joint_type ?? '').trim();
  if (fieldJointType) out.fieldJointType = fieldJointType;

  const blastStandard = String(record.blastStandard ?? record.blast_standard ?? '').trim();
  if (blastStandard) out.blastStandard = blastStandard;

  const extras = parseSowPipelineExtrasFromRecord(record);
  Object.assign(
    out,
    buildSowPipelineExtras({
      rootWeldingTechnique: extras.rootWeldingTechnique,
      hasInternalLiner: extras.hasInternalLiner,
      internalLinerType: extras.internalLinerType,
      hasInternalCoating: extras.hasInternalCoating,
      internalCoatingType: extras.internalCoatingType,
    })
  );

  Object.assign(out, buildWallThicknessApiFields(parseWallThicknessFromRecord(record)));

  const outerDiameterInch = readSowNumericField(record, 'outerDiameterInch', 'outer_diameter_inch');
  if (outerDiameterInch != null) out.outerDiameterInch = outerDiameterInch;

  const expectedPipeLength = readSowNumericField(record, 'expectedPipeLength', 'expected_pipe_length');
  if (expectedPipeLength != null) out.expectedPipeLength = expectedPipeLength;

  const actualPipelineLength = readSowNumericField(record, 'actualPipelineLength', 'actual_pipeline_length');
  if (actualPipelineLength != null) out.actualPipelineLength = actualPipelineLength;

  const actualWelds = readSowNumericField(record, 'actualWelds', 'actual_welds');
  if (actualWelds != null) out.actualWelds = actualWelds;

  return out;
};
